import React, { useState, useCallback } from 'react';
import { CloudStorageModal } from '../CloudStorageModal';
import { createStyles, makeStyles, Theme, Button } from '@material-ui/core';
import { List } from './List';
import { checkResData, createFile } from '../../utils/common';
import { useForm, UseFormReturn, FormProvider } from 'react-hook-form';
import {
  TDocumentListDefaultValues,
  TDocumentModal
} from '../../types/CloudStorageModal';
import { useDropzone } from 'react-dropzone';
import { TSelectItem } from '../../types/common';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import {
  openErrorFlg,
  openSuccessFlg,
  progressCountFlgState,
  progressCountState,
  progressFlgState,
  resMessage
} from '../../GlobalState';
import { useSnackbar } from 'notistack';
import { acceptableFileContentTypes } from '../../utils/common';

type Tprops = {
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  vendorsList: TSelectItem[];
  constructId: number;
  fileFolderId: number;
  getFolderFileList: any;
  fileTypes: TSelectItem[];
};

// 初期データ
const defaultValues: TDocumentModal = {
  list: []
};

/**
 * 登録用モーダル(資料)
 * @param props
 * @constructor
 */
export const AddModal: React.FC<Tprops> = (props: Tprops) => {
  const {
    modalOpen,
    setModalOpen,
    vendorsList,
    constructId,
    fileFolderId,
    getFolderFileList,
    fileTypes
  } = props;

  const construct_id = String(constructId);
  const file_folder_id = String(fileFolderId);
  const maxFileCount = 30;

  const { enqueueSnackbar } = useSnackbar();

  // スタイル読み込み
  const classes = useStyles();
  // ファイルデータ
  const [files, setFiles] = useState<File[]>([]);

  const [, setProgressFlg] = useRecoilState<boolean>(progressCountFlgState);

  const [, setProgressCount] = useRecoilState<number>(progressCountState);

  // hook-formの初期設定
  const methods: UseFormReturn<TDocumentModal, any> = useForm({
    defaultValues
  });
  const {
    setValue,
    getValues,
    handleSubmit,
    watch,
    formState: { errors }
  } = methods;

  const fileTypeAccepts = acceptableFileContentTypes();

  /**
   * ドラッグ&ドロップされた際の処理
   * @param acceptedFiles
   */
  const onDrop = (acceptedFiles: File[]) => {
    addFile(acceptedFiles);
  };

  // ドラッグ&ドロップの初期設定
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  /**
   * ファイル選択ボタン押下時
   * @param event
   */
  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 選択ファイル一覧を取得
    const fileList: FileList | null = event.target.files;

    // ファイルが選択されている場合
    if (fileList) {
      addFile(Array.from(fileList));
    }
    event.target.value = '';
  };

  const addFile = (fileList: File[]) => {
    const fileCount: number = fileList.length;

    if (fileCount <= 0) {
      return;
    }

    if (fileCount + files.length <= maxFileCount) {
      // FileList→File[]型に変換しすでに登録されているファイルに追加
      const filesData: File[] = files.concat(fileList);
      const addFileDatas: File[] = [];
      filesData.map(file => {
        let format = file.type;

        //「vwx」「dwg」「dxf」「jwc」の判定
        if (file && format == '') {
          if (file.name.endsWith('.dwg')) {
            format = 'image/vnd.dwg';
          } else if (file.name.endsWith('.vwx')) {
            format = 'application/vnd.vectorworks';
          } else if (file.name.endsWith('.jwc')) {
            format = 'application/jwc';
          } else if (file.name.endsWith('.dxf')) {
            format = 'application/dxf';
          }
        }

        if (file && fileTypeAccepts.includes(format)) {
          addFileDatas.push(file);
        } else {
          enqueueSnackbar(`規定外の拡張子です。 [ ${file.name} ]`, {
            variant: 'error'
          });
        }
      });

      setFiles(addFileDatas);
    } else {
      enqueueSnackbar(
        `現在計${
          fileCount + files.length
        }です。同時にアップロードできる資料数は${maxFileCount}枚までです`,
        { variant: 'error' }
      );
    }
  };

  /**
   * リセットボタン押下時（仮で実装しているから後々削除）
   */
  const resetFile = () => {
    setFiles([]);
    setValue('list', []);
  };

  /**
   * 送信ボタン押下時
   */
  const onSend = async () => {
    setProgressFlg(true);
    setProgressCount(0);

    // hook-formよりデータ取得
    const lists: TDocumentListDefaultValues[] = getValues('list');

    const count = 100 / lists.length;

    // データの件数分ループ処理
    for (let i = 0; lists.length > i; i++) {
      const list: TDocumentListDefaultValues = lists[i];

      // ファイルデータ取得
      const file: File = list.upload_file;

      // FormData作成
      const data = new FormData();

      // データをFormDataに登録
      data.append('upload_file', file as any);
      data.append('name', list.name);
      data.append('construct_id', construct_id);
      data.append('file_type_id', String(list.file_type_id.value));
      data.append('send_to', list.send_to ? String(list.send_to.value) : '');
      data.append(
        'receive_from',
        list.receive_from ? String(list.receive_from.value) : ''
      );
      data.append('exchange_date', list.exchange_date);
      data.append('file_folder_id', file_folder_id);
      data.append('description', list.description ? list.description : '');
      data.append('amount', list.amount ? list.amount : '');

      const sizeLimit = 1024 * 1024 * 100; // アップロード容量制限(100MB)
      // ファイルサイズが100MBを超えるファイルはアップロードできず、continueで次のループに進む
      if (file.size > sizeLimit) {
        alert(
          `${list.name}はアップロードできません\nファイルサイズは100MB以下にしてください。`
        );
        continue;
      }

      let format = file.type;

      //「vwx」「dwg」「dxf」「jwc」の判定
      if (file && format == '') {
        if (list.name.endsWith('.dwg')) {
          format = 'image/vnd.dwg';
        } else if (list.name.endsWith('.vwx')) {
          format = 'application/vnd.vectorworks';
        } else if (list.name.endsWith('.jwc')) {
          format = 'application/jwc';
        } else if (list.name.endsWith('.dxf')) {
          format = 'application/dxf';
        }
      }

      // ファイルデータの型が規定にあっているか判定
      if (file && fileTypeAccepts.includes(format)) {
        // 登録処理
        await createFile(data)
          .then(resData => {
            checkResData(resData, enqueueSnackbar).then((data: any) => {
              setModalOpen(false);
            });
          })
          .finally(() => {
            setProgressCount(prevProgress =>
              prevProgress >= 100 ? 0 : prevProgress + count
            );
          });
      } else {
        enqueueSnackbar(`規定外の拡張子です。 [ ${file.name} ]`, {
          variant: 'error'
        });
      }
    }

    getFolderFileList();
    resetFile();
    setProgressFlg(false);
  };

  /**
   * ×マーク押下時にフォイルを削除
   */
  const deleteFile = (index: number) => {
    const newFiles: File[] = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  return (
    <FormProvider {...methods}>
      <CloudStorageModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        onFileInputChange={onFileInputChange}
        resetFile={resetFile}
        onSend={onSend}
        title={'資料を追加'}
        handleSubmit={handleSubmit}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
      >
        <div className={classes.body}>
          <div className={classes.bodyListArea}>
            {files.length > 0 && (
              <div>
                選択枚数　
                <span style={{ fontWeight: 'bold' }}>
                  {files.length}/{maxFileCount}
                </span>
              </div>
            )}
            {files.map((file: File, index: number) => (
              <List
                key={file.name}
                file={file}
                index={index}
                setValue={setValue}
                vendorsList={vendorsList}
                deleteFile={deleteFile}
                fileTypes={fileTypes}
              />
            ))}
          </div>
        </div>
      </CloudStorageModal>
    </FormProvider>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    body: {
      backgroundColor: theme.palette.background.paper
    },
    bodyListArea: {
      maxHeight: '300px',
      width: '100%',
      overflowY: 'scroll',
      padding: theme.spacing(0, 4),
      boxSizing: 'border-box'
    }
  })
);
